import React, { useEffect, useRef } from 'react';
import { Box, Container, useToast } from '@chakra-ui/react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { CreateSiteProvider } from '../context/CreateSiteContext';
import { CreateSiteWizard } from '../components/create-site/CreateSiteWizard';

/**
 * CreateSite page component
 *
 * This page allows users to create new megalithic sites with the Explorer's Journal
 * interface that provides an immersive experience while collecting detailed information
 * about archaeological sites.
 *
 * The main features include:
 * - Journal-style wizard navigation
 * - AI research assistant integration
 * - Draft management system
 * - Rich media uploads
 * - Timeline builder
 */
const CreateSite = () => {
  const { user, isLoading } = useAuth();
  const toast = useToast();
  const { draftId } = useParams(); // Get draftId from URL params if present
  
  // Use refs to track if welcome message and draft loading have been done
  const welcomeShown = useRef(false);
  const draftLoadAttempted = useRef(false);

  useEffect(() => {
    // Display welcome message after authentication check completes
    // But only if it hasn't been shown already and we're not editing a draft
    if (!isLoading && user && !welcomeShown.current && !draftId) {
      welcomeShown.current = true;
      toast({
        title: "Welcome to the Explorer's Journal",
        description: "Document your megalithic discovery with our immersive site creation wizard.",
        status: "info",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
    }
  }, [isLoading, user, toast, draftId]);

  // Redirect to login if user is not authenticated
  if (!isLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  // Show nothing during the loading state
  if (isLoading) {
    return null;
  }

  return (
    <Box py={{ base: 4, md: 8 }}>
      <CreateSiteProvider>
        <CreateSiteWizard draftIdToLoad={draftId} />
      </CreateSiteProvider>
    </Box>
  );
};

export default CreateSite;
